import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import BackgroundFX from "@/components/BackgroundFX";
import KineticTitle from "@/components/KineticTitle";
import PlayButton from "@/components/PlayButton";
import Equalizer from "@/components/Equalizer";
import VolumeControl from "@/components/VolumeControl";
import InstallButton from "@/components/InstallButton";
import Marquee from "@/components/Marquee";
import { STREAM_URL, STATION_NAME, STATION_TAGLINE } from "@/config";

const STATUS_TEXT = {
  idle: "Premi play e lasciati portare",
  loading: "Sintonizzazione in corso\u2026",
  playing: "In diretta \u2014 Napoli Lounge",
  error: "Stream non raggiungibile, riprova",
  unsupported: "Questo browser non supporta il formato audio",
};

export default function App() {
  const audioRef = useRef(null);
  const [status, setStatus] = useState("idle");
  const [volume, setVolume] = useState(0.85);
  const playing = status === "playing";

  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing || status === "loading") {
      audio.pause();
      setStatus("idle");
      return;
    }
    setStatus("loading");
    try {
      await audio.play();
    } catch {
      setStatus("error");
    }
  };

  const toggleRef = useRef(togglePlay);
  toggleRef.current = togglePlay;

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onPlaying = () => setStatus("playing");
    const onWaiting = () =>
      setStatus((s) => (s === "playing" ? "loading" : s));
    const onError = () => {
      const err = audio.error;
      if (!err || err.code === 1) return;
      setStatus(err.code === 4 ? "unsupported" : "error");
    };
    audio.addEventListener("playing", onPlaying);
    audio.addEventListener("waiting", onWaiting);
    audio.addEventListener("error", onError);
    return () => {
      audio.removeEventListener("playing", onPlaying);
      audio.removeEventListener("waiting", onWaiting);
      audio.removeEventListener("error", onError);
    };
  }, []);

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    navigator.mediaSession.metadata = new MediaMetadata({
      title: STATION_NAME,
      artist: "In diretta",
      album: STATION_TAGLINE,
      artwork: [
        { src: "/icons/media-art-512.png", sizes: "512x512", type: "image/png" },
      ],
    });
    navigator.mediaSession.setActionHandler("play", () => toggleRef.current());
    navigator.mediaSession.setActionHandler("pause", () => toggleRef.current());
  }, []);

  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    navigator.mediaSession.playbackState = playing ? "playing" : "paused";
  }, [playing]);

  return (
    <div
      data-testid="app-screen"
      className="relative min-h-[100dvh] w-full bg-sky-100 text-sky-950 flex flex-col font-sans"
    >
      <BackgroundFX />
      <div className="grain-overlay" />
      <audio ref={audioRef} src={STREAM_URL} preload="none" />

      <main className="relative z-10 flex-1 flex flex-col items-center justify-center px-6">
        <KineticTitle />
        <motion.div
          initial={{ opacity: 0, scale: 0.88 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1.25, duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8 sm:mt-10 flex flex-col items-center gap-6"
        >
          <PlayButton status={status} onToggle={togglePlay} />
          <div className="h-5 flex items-center justify-center">
            {playing && <Equalizer />}
          </div>
          <p
            data-testid="status-text"
            className={`text-[10px] sm:text-xs tracking-[0.3em] uppercase transition-colors duration-500 ${
              status === "error" || status === "unsupported"
                ? "text-red-500"
                : "text-sky-600"
            }`}
          >
            {STATUS_TEXT[status]}
          </p>
          <VolumeControl volume={volume} onChange={setVolume} />
        </motion.div>
      </main>

      <footer className="relative z-20 pb-6 sm:pb-8 flex flex-col items-center gap-6">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.55, duration: 0.9, ease: "easeOut" }}
          className="flex flex-col sm:flex-row items-center gap-5 sm:gap-8"
        >
          <InstallButton />
        </motion.div>
        <motion.img
          src="/logo-summer.png"
          alt="Enjoy the summer"
          data-testid="summer-logo"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.75, duration: 0.9, ease: "easeOut" }}
          className="h-16 sm:h-20 w-auto"
        />
        <Marquee />
      </footer>
    </div>
  );
}
